import React from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

export default function ResetSuccessScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.card}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>{'\u2705'}</Text>
        </View>
        <Text style={styles.title}>Account Reset Complete</Text>
        <Text style={styles.subtitle}>
          Your account has been reset successfully. Here's what happened:
        </Text>

        <View style={styles.checklist}>
          <View style={styles.checkItem}>
            <Text style={styles.checkIcon}>{'\u2714'}</Text>
            <Text style={styles.checkText}>Old encryption key removed</Text>
          </View>
          <View style={styles.checkItem}>
            <Text style={styles.checkIcon}>{'\u2714'}</Text>
            <Text style={styles.checkText}>All encrypted data deleted</Text>
          </View>
          <View style={styles.checkItem}>
            <Text style={styles.checkIcon}>{'\u2714'}</Text>
            <Text style={styles.checkText}>Account identity preserved</Text>
          </View>
          <View style={styles.checkItem}>
            <Text style={styles.checkIcon}>{'\u2714'}</Text>
            <Text style={styles.checkText}>Settings and plan kept</Text>
          </View>
        </View>

        <Text style={styles.nextSteps}>
          Next, you'll set up a new encryption key and recovery phrase to
          protect your fresh start.
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={() => router.replace('/(setup)/encryption')}
          activeOpacity={0.7}
          accessibilityLabel="Set up new encryption"
          accessibilityRole="button"
          testID="reset-success.setup-button"
        >
          <Text style={styles.buttonText}>Set Up Encryption</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.brandedBackground },
  scrollContent: {
    flexGrow: 1 as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.successBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  checklist: {
    backgroundColor: theme.colors.successBg,
    borderRadius: 10,
    padding: 14,
    marginBottom: 20,
  },
  checkItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    marginBottom: 10,
  },
  checkIcon: { fontSize: 14, color: theme.colors.success, marginRight: 10, width: 20 },
  checkText: { fontSize: 14, color: theme.colors.successText, flex: 1 },
  nextSteps: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  button: {
    backgroundColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 48,
  },
  buttonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
});
